import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Heart,
  Info,
  PlayCircle,
  PauseCircle,
  Clock,
  Award,
  HelpCircle,
  BarChart2,
  CheckCircle,
  AlertCircle,
  XCircle,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc, increment } from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const BreathPacer = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game modes
  const [gameMode, setGameMode] = useState("intro"); // intro, tutorial, play, results
  const [showInstructions, setShowInstructions] = useState(false);
  const [sessionCompleted, setSessionCompleted] = useState(false);

  // Breathing settings
  const breathCycles = 10; // Fixed number of breath cycles in a session
  const inhaleTime1 = 1500; // First inhale (ms) - 1.5 seconds
  const inhaleTime2 = 500; // Second inhale (ms) - 0.5 seconds
  const exhaleTime = 4000; // Exhale time (ms) - 4 seconds
  const pauseTime = 500; // Pause between cycles (ms)

  // Game state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [cyclePhase, setCyclePhase] = useState(0); // 0 = first inhale, 1 = second inhale, 2 = exhale, 3 = pause
  const [currentCycle, setCurrentCycle] = useState(0);
  const [breathPercentage, setBreathPercentage] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionEndTime, setSessionEndTime] = useState(null);
  const [showPointsAwarded, setShowPointsAwarded] = useState(false);

  // Stats tracking
  const [completedSessions, setCompletedSessions] = useState(0);
  const [totalBreathCycles, setTotalBreathCycles] = useState(0);
  const [perfectCycles, setPerfectCycles] = useState(0);
  const [avgCoherence, setAvgCoherence] = useState(0);
  const [coherenceScores, setCoherenceScores] = useState([]);
  const [cycleCoherence, setCycleCoherence] = useState(0);

  // Timer refs
  const timerRef = useRef(null);
  const phaseTimerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const pausedStateRef = useRef(false);
  const currentTimeoutsRef = useRef([]);

  // User guidance
  const [userFeedback, setUserFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState("neutral"); // "good", "warning", "error", "neutral"

  // Tutorial state
  const [tutorialStep, setTutorialStep] = useState(0);

  // Timer settings and controls
  const [timeLeft, setTimeLeft] = useState(calculateTotalTime());

  // Clear all timeouts
  const clearAllTimeouts = () => {
    currentTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    currentTimeoutsRef.current = [];
  };

  // Safely set a timeout that we can track and clear if needed
  const safeSetTimeout = (callback, delay) => {
    if (!isPlayingRef.current || pausedStateRef.current) return null;

    const timeoutId = setTimeout(() => {
      if (!isPlayingRef.current || pausedStateRef.current) return;
      callback();
    }, delay);

    currentTimeoutsRef.current.push(timeoutId);
    return timeoutId;
  };

  // Calculate total session time based on current settings
  function calculateTotalTime() {
    const cycleTime = inhaleTime1 + inhaleTime2 + exhaleTime + pauseTime;
    return Math.ceil((cycleTime * breathCycles) / 1000); // Return in seconds
  }

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Timer logic
  useEffect(() => {
    if (isPlaying && !isPaused) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endSession(true);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [isPlaying, isPaused]);

  // Clean up all timers on unmount
  useEffect(() => {
    return () => {
      console.log("Cleaning up all timers");
      clearInterval(timerRef.current);
      clearAllTimeouts();
      isPlayingRef.current = false;
    };
  }, []);

  // Function to run a full breath cycle with simple timeouts
  const runBreathCycle = (cycleNum) => {
    if (
      cycleNum >= breathCycles ||
      !isPlayingRef.current ||
      pausedStateRef.current
    ) {
      if (cycleNum >= breathCycles) {
        endSession(true);
      }
      return;
    }

    console.log(`Starting breath cycle ${cycleNum + 1}`);
    setCurrentCycle(cycleNum);

    // Phase 1: First inhale (expand from 0% to 50%)
    setCyclePhase(0);
    setUserFeedback("First Inhale: Deep breath through nose");
    setShowFeedback(true);

    // First inhale animation - gradual expansion from 0% to 50%
    animateBreathGradually(0, 50, inhaleTime1, () => {
      // Add a brief pause between inhales to make the transition noticeable
      safeSetTimeout(() => {
        // Phase 2: Second inhale (expand from 50% to 70%)
        setCyclePhase(1);
        setUserFeedback("Second Inhale: Small additional breath");
        setShowFeedback(true);

        // Second inhale animation - quick expansion from 50% to 70%
        // Using a shorter duration for the second inhale to make it more distinct
        animateBreathGradually(50, 70, inhaleTime2, () => {
          // Phase 3: Exhale (contract from 70% to 0%)
          setCyclePhase(2);
          setUserFeedback("Long, slow exhale");
          setShowFeedback(true);

          // Exhale animation - gradual contraction from 70% to 0%
          animateBreathGradually(70, 0, exhaleTime, () => {
            // Phase 4: Pause
            setCyclePhase(3);
            setUserFeedback("Brief pause");
            setShowFeedback(true);

            // Move to next cycle after pause
            safeSetTimeout(() => {
              if (isPlayingRef.current && !pausedStateRef.current) {
                runBreathCycle(cycleNum + 1);
              }
            }, pauseTime);
          });
        });
      }, 200); // Add a brief 200ms pause to make the transition between inhales more distinct
    });
  };

  // Add this new function for smooth breath animation
  const animateBreathGradually = (
    startValue,
    endValue,
    duration,
    onComplete,
    isPauseTransition = false
  ) => {
    // Number of steps for smooth animation (approximately 30fps)
    const fps = 30;
    const steps = Math.ceil((duration / 1000) * fps);
    const stepValue = (endValue - startValue) / steps;
    const stepDuration = duration / steps;

    // Function to perform a single animation step
    const performStep = (currentStep, currentValue) => {
      if (
        !isPlayingRef.current ||
        pausedStateRef.current ||
        currentStep >= steps
      ) {
        if (
          isPlayingRef.current &&
          !pausedStateRef.current &&
          currentStep >= steps
        ) {
          // Animation completed successfully
          setBreathPercentage(endValue);
          if (onComplete) onComplete();
        }
        return;
      }

      // Calculate the next value using easing for more natural movement
      const newValue = currentValue + stepValue;
      setBreathPercentage(newValue);

      // Schedule the next step
      const timeoutId = setTimeout(() => {
        performStep(currentStep + 1, newValue);
      }, stepDuration);

      // Track the timeout so it can be cleared if needed
      currentTimeoutsRef.current.push(timeoutId);
    };

    // Start the animation
    setBreathPercentage(startValue);
    performStep(0, startValue);
  };

  // Start a new session
  const startSession = () => {
    console.log("Starting breath training session...");

    // Reset all session state
    setCurrentCycle(0);
    setCyclePhase(0);
    setBreathPercentage(0);
    setCoherenceScores([]);
    setShowFeedback(false);

    // Update timer
    setTimeLeft(calculateTotalTime());

    // Start animation loop
    setIsPlaying(true);
    isPlayingRef.current = true;
    setIsPaused(false);
    pausedStateRef.current = false;
    setSessionStartTime(Date.now());

    // Set initial feedback
    setUserFeedback("Breathe in deeply");
    setFeedbackType("neutral");
    setShowFeedback(true);

    // Clear any existing timeouts just in case
    clearAllTimeouts();

    // Begin the first breath cycle
    setTimeout(() => {
      runBreathCycle(0);
    }, 100);
  };

  // Pause/resume session
  const togglePause = () => {
    if (isPaused) {
      // Resume
      console.log("Resuming breath cycles");
      setIsPaused(false);
      pausedStateRef.current = false;
      runBreathCycle(currentCycle); // Restart from current cycle
    } else {
      // Pause
      console.log("Pausing breath cycles");
      setIsPaused(true);
      pausedStateRef.current = true;
      clearAllTimeouts(); // Stop all pending timeouts
    }
  };

  // End the session
  const endSession = (completed = false) => {
    console.log("Ending breath training session...");

    // Clear timers
    clearAllTimeouts();
    clearInterval(timerRef.current);
    isPlayingRef.current = false;
    setIsPlaying(false);
    setIsPaused(false);
    pausedStateRef.current = false;

    // Mark session as complete only if it was completed naturally
    setSessionCompleted(completed);

    setSessionEndTime(Date.now());

    const totalCoherence = coherenceScores.reduce(
      (sum, score) => sum + score,
      0
    );
    const avgCoherenceValue =
      coherenceScores.length > 0 ? totalCoherence / coherenceScores.length : 0;

    setAvgCoherence(avgCoherenceValue);
    setCompletedSessions((prev) => prev + 1);
    setTotalBreathCycles((prev) => prev + breathCycles);

    const perfectCount = coherenceScores.filter((score) => score > 0.8).length;
    setPerfectCycles((prev) => prev + perfectCount);

    setGameMode("results");
  };

  // Reward user with points
  const awardPoints = async () => {
    if (!organizationId || !user?.email || !sessionCompleted) {
      // Don't award points if session didn't complete
      setUserFeedback("Complete the full session to earn points");
      setFeedbackType("warning");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 2000);
      return;
    }

    try {
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      await updateDoc(userDocRef, {
        gameScore: increment(40),
      });

      setShowPointsAwarded(true);
      setTimeout(() => setShowPointsAwarded(false), 3000);
    } catch (error) {
      console.error("Error awarding points:", error);
      setUserFeedback("Error awarding points");
      setFeedbackType("error");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 2000);
    }
  };

  // Handle the tutorial steps
  const advanceTutorial = () => {
    if (tutorialStep < 4) {
      setTutorialStep((prev) => prev + 1);
    } else {
      // End tutorial and start the game
      setGameMode("play");
      startSession();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-rose-300">
              Breath Pacer
            </h1>
            <div className="p-2 bg-rose-500/10 rounded-full">
              <Heart className="text-rose-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-500/10 rounded-lg">
                      <Heart className="text-rose-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Breath Pacer Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-rose-300 font-medium mb-2">
                      Breath Pattern Overview
                    </h3>
                    <p className="text-slate-300">
                      Breath Pacer uses the "Physiological Sigh" breathing
                      technique, a scientifically validated pattern that
                      activates the parasympathetic nervous system and helps
                      regulate stress. This pattern consists of two inhales
                      through the nose followed by a long exhale. Exhale twice
                      as long as you inhale to properly do this breathing
                      technique.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-rose-300 font-medium mb-2">
                      How to Practice
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-slate-300">
                      <li>
                        Follow the animated circle as it expands and contracts
                      </li>
                      <li>
                        First inhale: Take a deep breath in through your nose
                        (1.5 seconds)
                      </li>
                      <li>
                        Second inhale: Take another small inhale to stack air
                        (0.5 second)
                      </li>
                      <li>
                        Exhale: Slowly release all the air through your mouth (4
                        seconds)
                      </li>
                      <li>Pause briefly before beginning the next cycle</li>
                      <li>Maintain this rhythm for all 10 breath cycles</li>
                    </ol>
                  </div>

                  <div>
                    <h3 className="text-rose-300 font-medium mb-2">
                      Recovery Benefits
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-slate-300">
                      <li>
                        Increases heart rate variability (HRV), a biomarker of
                        autonomic nervous system health
                      </li>
                      <li>Reduces stress hormone levels (cortisol)</li>
                      <li>
                        Improves emotional regulation by activating
                        parasympathetic response
                      </li>
                      <li>
                        Helps reduce cravings by regulating the body's stress
                        response
                      </li>
                      <li>
                        Enhances focus and decision-making capacity under stress
                      </li>
                    </ul>
                  </div>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-rose-600 hover:to-pink-700 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Points Awarded Popup */}
        <AnimatePresence>
          {showPointsAwarded && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50"
            >
              <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
                <Award size={24} />
                <div>
                  <p className="font-bold">Points Awarded!</p>
                  <p>You earned 40 points for completing this practice.</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Heart size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Breath Pacer
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Practice the Physiological Sigh breathing technique to rapidly
                  calm your nervous system, reduce stress, and regulate
                  emotions.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-rose-500/10 rounded-full mb-2">
                    <Heart size={24} className="text-rose-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">Rapid Calming</h3>
                  <p className="text-sm text-slate-300 text-center">
                    The fastest way to slow your heart rate in real time
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-rose-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-rose-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">1-3 minutes</h3>
                  <p className="text-sm text-slate-300 text-center">
                    10 breath cycles for optimal stress reduction
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-rose-500/10 rounded-full mb-2">
                    <Award size={24} className="text-rose-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Stanford Research
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Studied and recommended by leading neuroscientists. Shown to
                    lower anxiety, improve mood, and decrease resting breathing
                    rates.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("tutorial")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <HelpCircle size={18} />
                  Tutorial
                </button>

                <button
                  onClick={() => {
                    setGameMode("play");
                    startSession();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Start Practice
                </button>
              </div>
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Physiological Sigh Breathing
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        The Physiological Sigh is a breathing technique
                        researched by Dr. Andrew Huberman that triggers your
                        body's relaxation response and helps regulate emotions.
                      </p>
                      <p className="text-slate-300">
                        Let's walk through how to perform this technique
                        properly. I'll guide you through each phase of the
                        breath pattern.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        Notice the expanding circle animation - it will guide
                        your breathing rhythm. The first inhale takes about 1.5
                        seconds.
                      </p>
                      // In tutorialStep === 2 section:
                      <p className="text-slate-300">
                        The second inhale is shorter - only about 0.5 second.
                        The circle will expand just a bit more to indicate this
                        second inhale.
                      </p>
                      // In tutorialStep === 3 section:
                      <p className="text-slate-300">
                        The exhale should take approximately 4 seconds. The
                        circle will gradually shrink during this phase. Keep in
                        mind, what is more important is that your exhales are at
                        least twice as long as your inhales.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        <span className="text-rose-300 font-medium">
                          Step 2: The Second Inhale
                        </span>
                      </p>
                      <p className="text-slate-300">
                        Immediately follow with a second, shorter inhale through
                        your nose to completely fill your lungs to 100%
                        capacity. This activates additional alveoli (air sacs)
                        in your lungs.
                      </p>
                      <p className="text-slate-300">
                        The second inhale is shorter - only about 1 second. The
                        circle will expand just a bit more to indicate this
                        second inhale.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        <span className="text-rose-300 font-medium">
                          Step 3: The Long Exhale
                        </span>
                      </p>
                      <p className="text-slate-300">
                        Now, slowly exhale through your mouth, making the exhale
                        about twice as long as the combined inhales. This
                        extended exhale activates your parasympathetic nervous
                        system, slowing your heart rate.
                      </p>
                      <p className="text-slate-300">
                        The exhale should take approximately 6 seconds. The
                        circle will gradually shrink during this phase.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 4 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        <span className="text-rose-300 font-medium">
                          The Science Behind It
                        </span>
                      </p>
                      <p className="text-slate-300">
                        When you exhale longer than you inhale, you activate the
                        vagus nerve, which helps regulate your parasympathetic
                        "rest and digest" nervous system. This helps reduce
                        stress, anxiety, and can even impact cravings.
                      </p>
                      <p className="text-slate-300">
                        Regular practice improves your heart rate variability
                        (HRV), which is associated with better stress
                        resilience, emotional regulation, and cognitive
                        performance - all critical for recovery.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tutorial visualization */}
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="flex-1 flex justify-center">
                  <div className="relative w-64 h-64 flex items-center justify-center">
                    {/* Background circle */}
                    <div className="absolute inset-0 rounded-full border-2 border-slate-700 opacity-30"></div>

                    {/* Animated breathing circle */}
                    <div
                      className={`absolute rounded-full bg-gradient-to-r from-rose-500/20 to-pink-600/20 border-2 border-rose-400 transition-all duration-1000 ease-in-out ${
                        tutorialStep === 1
                          ? "scale-[0.7]" // First inhale
                          : tutorialStep === 2
                          ? "scale-[0.9]" // Second inhale
                          : tutorialStep === 3
                          ? "scale-[0.2]" // Exhale
                          : "scale-[0.4]" // Default
                      }`}
                      style={{
                        width: "100%",
                        height: "100%",
                        transformOrigin: "center",
                      }}
                    ></div>

                    {/* Current phase indicator */}
                    <div className="text-center z-10">
                      <div className="text-xl font-medium text-rose-300">
                        {tutorialStep === 1
                          ? "First Inhale"
                          : tutorialStep === 2
                          ? "Second Inhale"
                          : tutorialStep === 3
                          ? "Long Exhale"
                          : "Breath Pacer"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  {/* Breathing phases diagram */}
                  <div className="bg-slate-700/30 p-4 rounded-lg">
                    <h3 className="text-white font-medium mb-3">
                      Breath Pattern:
                    </h3>
                    <div className="flex items-center gap-2 justify-between">
                      <div
                        className={`p-3 rounded-lg text-center ${
                          tutorialStep === 1
                            ? "bg-rose-500/30 border border-rose-500"
                            : "bg-slate-800"
                        }`}
                      >
                        <p className="text-sm text-slate-300">First Inhale</p>
                        <p className="font-mono text-white">1.5 - 2 sec</p>
                      </div>
                      <ChevronRight size={16} className="text-slate-500" />
                      <div
                        className={`p-3 rounded-lg text-center ${
                          tutorialStep === 2
                            ? "bg-rose-500/30 border border-rose-500"
                            : "bg-slate-800"
                        }`}
                      >
                        <p className="text-sm text-slate-300">Second Inhale</p>
                        <p className="font-mono text-white">.5 - 1 sec</p>
                      </div>
                      <ChevronRight size={16} className="text-slate-500" />
                      <div
                        className={`p-3 rounded-lg text-center ${
                          tutorialStep === 3
                            ? "bg-rose-500/30 border border-rose-500"
                            : "bg-slate-800"
                        }`}
                      >
                        <p className="text-sm text-slate-300">Exhale</p>
                        <p className="font-mono text-white">4-6 sec</p>
                      </div>
                    </div>
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex justify-between">
                    {tutorialStep > 0 ? (
                      <button
                        onClick={() => setTutorialStep((prev) => prev - 1)}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Previous
                      </button>
                    ) : (
                      <button
                        onClick={() => setGameMode("intro")}
                        className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      >
                        Back
                      </button>
                    )}

                    <button
                      onClick={advanceTutorial}
                      className="py-2 px-4 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-lg transition-colors"
                    >
                      {tutorialStep < 4 ? "Next" : "Start Practice"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Game Play Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-rose-500/20 to-pink-600/20 px-4 py-2 rounded-lg">
                  <span className="text-rose-300 font-medium">
                    Physiological Sigh
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-gradient-to-r from-rose-500/20 to-pink-600/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Cycle: {currentCycle}/{breathCycles}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
                {/* Breathing visualization */}
                <div className="relative">
                  <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center">
                    {/* Background circle */}
                    <div className="absolute inset-0 rounded-full border-2 border-slate-700 opacity-30"></div>

                    {/* Animated breathing circle */}
                    <div
                      className="absolute rounded-full bg-gradient-to-r from-rose-500/20 to-pink-600/20 border-2 border-rose-400"
                      style={{
                        width: "100%",
                        height: "100%",
                        transform: `scale(${breathPercentage / 100})`,
                        transformOrigin: "center",
                        // No transition property - we control animation in JS now
                      }}
                    ></div>

                    {/* Current phase indicator */}
                    <div className="text-center z-10">
                      <div className="text-xl font-medium text-rose-300">
                        {cyclePhase === 0
                          ? "First Inhale"
                          : cyclePhase === 1
                          ? "Second Inhale"
                          : cyclePhase === 2
                          ? "Long Exhale"
                          : "Brief Pause"}
                      </div>
                    </div>
                  </div>

                  {/* Feedback overlay */}
                  <AnimatePresence>
                    {showFeedback && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 right-0 mt-4 text-center"
                      >
                        <div
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                            feedbackType === "good"
                              ? "bg-green-500/20 text-green-400"
                              : feedbackType === "warning"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : feedbackType === "error"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-rose-500/20 text-rose-300"
                          }`}
                        >
                          {feedbackType === "good" ? (
                            <CheckCircle size={18} />
                          ) : feedbackType === "warning" ? (
                            <AlertCircle size={18} />
                          ) : feedbackType === "error" ? (
                            <XCircle size={18} />
                          ) : (
                            <Info size={18} />
                          )}
                          <span>{userFeedback}</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Game controls */}
                <div className="md:w-1/3 space-y-6">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">
                      Session Progress
                    </h3>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-400 text-sm">
                        Breath Cycles
                      </span>
                      <span className="text-white font-medium">
                        {currentCycle}/{breathCycles}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-rose-500 to-pink-600"
                        style={{
                          width: `${(currentCycle / breathCycles) * 100}%`,
                        }}
                      ></div>
                    </div>

                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-400 text-sm">
                          Coherence
                        </span>
                        <span className="text-white font-medium">
                          {(cycleCoherence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-400"
                          style={{
                            width: `${cycleCoherence * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={togglePause}
                      className={`flex-1 py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        isPaused
                          ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          : "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                      }`}
                    >
                      {isPaused ? (
                        <>
                          <PlayCircle size={20} />
                          Resume
                        </>
                      ) : (
                        <>
                          <PauseCircle size={20} />
                          Pause
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => {
                        if (
                          confirm(
                            "Are you sure you want to end the session early?"
                          )
                        ) {
                          endSession(false); // ❌ Manual end = no reward
                        }
                      }}
                      className="py-3 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                    >
                      End Session
                    </button>
                  </div>

                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                      <Info size={16} className="text-rose-400" />
                      Current Phase
                    </h3>

                    <ul className="space-y-1 text-sm">
                      <li
                        className={`flex items-center gap-2 ${
                          cyclePhase === 0 ? "text-rose-300" : "text-slate-400"
                        }`}
                      >
                        {cyclePhase === 0 ? (
                          <CheckCircle size={16} className="text-rose-400" />
                        ) : (
                          <div className="w-4 h-4"></div>
                        )}
                        First Inhale: Deep breath through nose (1.5s)
                      </li>
                      <li
                        className={`flex items-center gap-2 ${
                          cyclePhase === 1 ? "text-rose-300" : "text-slate-400"
                        }`}
                      >
                        {cyclePhase === 1 ? (
                          <CheckCircle size={16} className="text-rose-400" />
                        ) : (
                          <div className="w-4 h-4"></div>
                        )}
                        Second Inhale: Small additional breath (0.5s)
                      </li>
                      <li
                        className={`flex items-center gap-2 ${
                          cyclePhase === 2 ? "text-rose-300" : "text-slate-400"
                        }`}
                      >
                        {cyclePhase === 2 ? (
                          <CheckCircle size={16} className="text-rose-400" />
                        ) : (
                          <div className="w-4 h-4"></div>
                        )}
                        Exhale: Slow release through mouth (4s)
                      </li>
                      <li
                        className={`flex items-center gap-2 ${
                          cyclePhase === 3 ? "text-rose-300" : "text-slate-400"
                        }`}
                      >
                        {cyclePhase === 3 ? (
                          <CheckCircle size={16} className="text-rose-400" />
                        ) : (
                          <div className="w-4 h-4"></div>
                        )}
                        Pause: Brief rest before next cycle
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Award size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Practice Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed your Physiological Sigh practice session.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Award size={20} className="text-rose-400" />
                    Your Results
                  </h3>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Practice Reward</span>
                      <span className="text-2xl font-bold text-rose-300">
                        40 Points
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Breath Cycles</span>
                      <span className="text-white">
                        {currentCycle}/{breathCycles}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Average Coherence</span>
                      <span className="text-green-400">
                        {(avgCoherence * 100).toFixed(0)}%
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Session Duration</span>
                      <span className="text-white">
                        {sessionEndTime && sessionStartTime
                          ? Math.floor(
                              (sessionEndTime - sessionStartTime) / 1000 / 60
                            ) +
                            " min " +
                            Math.floor(
                              ((sessionEndTime - sessionStartTime) / 1000) % 60
                            ) +
                            " sec"
                          : "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-center mt-2">
                      <button
                        onClick={awardPoints}
                        className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <Award size={18} />
                        Claim 40 Points
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-rose-400" />
                    Benefits of Your Practice
                  </h3>

                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">
                          Stress Reduction
                        </span>
                        <span className="text-white text-sm">
                          {(avgCoherence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-400"
                          style={{ width: `${avgCoherence * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Science-Based Benefits From Your Session
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-rose-500/10 rounded-full mt-0.5">
                            <Heart size={14} className="text-rose-400" />
                          </div>
                          <span className="text-slate-300">
                            Activated the parasympathetic nervous system to
                            reduce stress
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-rose-500/10 rounded-full mt-0.5">
                            <Heart size={14} className="text-rose-400" />
                          </div>
                          <span className="text-slate-300">
                            Increased heart rate variability (HRV) for better
                            emotional regulation
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-rose-500/10 rounded-full mt-0.5">
                            <Heart size={14} className="text-rose-400" />
                          </div>
                          <span className="text-slate-300">
                            Released CO₂ trapped in the lungs, helping to reset
                            and regulate breathing
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-rose-500/10 rounded-full mt-0.5">
                            <Heart size={14} className="text-rose-400" />
                          </div>
                          <span className="text-slate-300">
                            Supported craving management through stress
                            regulation
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                <button
                  onClick={() => {
                    setGameMode("play");
                    startSession();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Practice Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreathPacer;
